import { Subscription } from "rxjs";
import { inject, injectable, singleton } from "tsyringe";
import { ExtensionContext } from "../../common/models/extension-config";
import { Module } from "../../common/module";
import { CommonModule } from "../common.module";
import { ApiModule } from "../common/api/api.module";
import { ServerAlertScheduleType, ServerAlertStatisticInputDTO, ServerAlertStatisticType, SimpleTableauServerAlertDTO } from "../common/api/autogenerated";
import { RequestInterceptorModule } from "../common/request-interceptor.module";
import { UiModule } from "../common/ui.module";
import { UrlChangeListenerModule } from "../common/url-change-listener.module";
import ServerAlert from "./ServerAlert.vue";
import { createApp } from 'vue';
@singleton()
export class ServerAlertsModule extends Module {
    public name: string = 'ServerAlertsModule';
    public acceptedLicenseFlags: string[] = ['TableauServerAlerts'];

    constructor(
        @inject(CommonModule) private common: CommonModule,
        @inject(ApiModule) private apiModule: ApiModule,
        @inject(RequestInterceptorModule) private requestInterceptor: RequestInterceptorModule,
        @inject(UrlChangeListenerModule) private urlChangeListener: UrlChangeListenerModule,
        @inject(UiModule) private uiModule: UiModule) {
        super([common, apiModule, urlChangeListener, requestInterceptor, uiModule]);
    }


    public shouldRun(context: ExtensionContext): boolean {
        return context === ExtensionContext.vizportal || this.common.isStandaloneVizQL()
    }

    private localDismissManager = new LocalDismissManager();
    // alert container ID
    private containerId = 'stm-alert-container';
    // the name of the local storage key that we use to notify other windows that a notification has been hidden
    private localStorageEventName = 'stm-noti-dismissed';
    public async run(): Promise<void> {

        // get and start displaying alerts
        this.getAlerts();

        // Handle alert closed on other windows - a localstorage event is used to notify of the alert that is dismissed. Find it and hide it
        window.addEventListener('storage', e => {
            if (e.key !== this.localStorageEventName) {
                return;
            }
            // try to find alert
            const alertElement = document.getElementById(`stm-alert-${e.newValue}`);
            if (alertElement) {
                this.hideAlert(alertElement);
            }
        })
        // if tableau site changes or user, get and start displaying alerts again (different alerts might be active for each site and each user might see different alerts)
        let lastRequestFailed: boolean | undefined = undefined;
        this.requestInterceptor.onTableauRequest.subscribe(request => {
            if (!request.dataObject) {
                return;
            }
            if (request.dataObject.method !== 'switchSite' && request.dataObject.method !== 'getSessionInfo') {
                return;
            }
            request.request.addEventListener('load', () => {
                const success = request.request.status === 200;
                // if request failed last time and now too, return
                if (!success && lastRequestFailed == false) {
                    return;
                }
                // get and draw alerts again
                this.getAlerts();
            })
        })

    }

    /**
     * Retrieves alerts from the backend, gets the relevant ones (excluding Regex and Schedule), gets their HTML and starts a loop to draw them (filter them first)
     */
    private async getAlerts() {

        const alertInfos = await this.apiModule.apiRequest(c => c.apiTableauServerAlertsClientGetAlerts(window.stm.config.serverId, window.stm.config.context === ExtensionContext.vizql));
        if (!alertInfos.success) {
            this.log.e('Failed to get alert infos', alertInfos.error);
            return;
        }
        // if we are unauthenticated, filter out events based on local dismiss storae
        if (!alertInfos.value!.isAuthenticated) {
            const dismissedIds = this.localDismissManager.getDismissedAlertIds();
            alertInfos.value!.alerts = alertInfos.value!.alerts!.filter(alert => !dismissedIds.includes(alert.id!));
        }
        // get the HTMLs for all alerts
        const alerts = alertInfos.value!.alerts!.map(alert => {

            const alertDom = document.createElement('div');
            createApp(ServerAlert, {
                info: alert
            }).mount(alertDom);
            alertDom.id = `stm-alert-${alert.id}`;
            alertDom.setAttribute('class', 'stm-alert-wrapper hidden');
            const alertInfo: AlertInfo = {
                alert: alert,
                element: alertDom
            };
            const closeButton = this.common.queryElement(alertDom, '.close');
            if (closeButton) {
                closeButton.onclick = () => this.dismissAlert(alertInfo);
            }
            return alertInfo;
           
        });

        // call updateAlerts to draw alerts that are needed
        this.displayAlertsLoop(alerts);
    }

    private hideAlert(alertElement: HTMLElement) {
        alertElement.className = 'stm-alert-wrapper hidden';
    }
    private showAlert(alertElement: HTMLElement) {
        alertElement.className = 'stm-alert-wrapper visible';
    }

    private async dismissAlert(alertInfo: AlertInfo) {
        this.hideAlert(alertInfo.element);
        const dismissRequest = await this.apiModule.apiRequest(c => c.apiTableauServerAlertsClientDismiss(alertInfo.alert.id));
        if (!dismissRequest.success) {
            this.showAlert(alertInfo.element);
            this.log.e('Failed to dismiss alert', alertInfo);
            return;
        }
        // notify localstorage that alert has been dismissed - this is needed so other tabs can pick it up
        window.localStorage.setItem(this.localStorageEventName, alertInfo.alert.id!);
        // mark the alert as dismissed in localstorage as well
        const dismissed = this.localDismissManager.getDismissedAlertIds();
        dismissed.push(alertInfo.alert.id!);
        this.localDismissManager.setDismissedAlertIds(dismissed);
        // if we are collecting stats of the alert, notify it
        if (alertInfo.alert.collectStatistics) {
            this.storeStatistics([alertInfo.alert], ServerAlertStatisticType.Dismissed);
        }
    }

    private autoHideAlert(alertInfo: AlertInfo) {
        if (alertInfo.alert.disappearAfterSec) {
            setTimeout(() => this.hideAlert(alertInfo.element), alertInfo.alert.disappearAfterSec * 1000);
        }
    }

    private async storeStatistics(alerts: SimpleTableauServerAlertDTO[], type: ServerAlertStatisticType) {
        if (!alerts?.length) {
            return;
        }
        const apiReq = await this.apiModule.apiRequest(e => e.apiTableauServerAlertsClientStoreStatistics(window.stm.config.serverId, alerts.map(alert =>
            new ServerAlertStatisticInputDTO({
                alertId: alert.id!,
                type,
                browserUrl: window.location.href,
                browserUserAgent: navigator.userAgent
            })
        )));
        if (!apiReq.success) {
            this.log.w('Failed to store alert statistics', apiReq.error);
        }

    }


    private hashChangeSub: Subscription | undefined;
    private timerSub: NodeJS.Timer | undefined;
    /**
     * Starts a loop to display filtered alerts.
     * Filtered alerts are redrawn every hash change (in case they have regex filter) and every second (in case they have a schedule)
     * @param alertInfos 
     */
    private displayAlertsLoop(alertInfos: AlertInfo[]) {
        // we have the list of all alerts to show, including the ones that are not scheduled or shouldn't show on this page url.
        // display the filtered alerts immidiately
        this.showFilteredAlerts(alertInfos);
        // subscribe for hash change to re-show filtered alerts
        if (this.hashChangeSub) {
            this.hashChangeSub.unsubscribe();
        }
        this.hashChangeSub = this.urlChangeListener.event.subscribe(async () => {
            await this.common.requestAnimationFrame();
            this.showFilteredAlerts(alertInfos);
        });
        // subsribe for timer to re-show filtered alerts
        if (this.timerSub) {
            clearInterval(this.timerSub);
        }
        this.timerSub = setInterval(() => this.showFilteredAlerts(alertInfos), 990)
    }


    /**
     * Shows filtered alerts - this is triggered every sec and navigation change to work with filtered alerts
     * @param alertInfos 
     */
    private showFilteredAlerts(alertInfos: AlertInfo[]) {

        const results: AlertInfo[] = [];
        // go through all alerts
        alertInfos.forEach(a => {
            // if alert has regexes
            if (a.alert.filter_UrlRegexes?.length) {
                // if none of the regexes match the current URL, skip this alert
                if (!a.alert.filter_UrlRegexes.some(regex => new RegExp(regex).test(window.location.href))) {
                    return;
                }
            }
            // if alert has a schedule
            if (a.alert.schedule?.length) {
                const now = new Date();
                const currentTime = now.getUTCHours() + ":" + now.getUTCMinutes() + ":" + now.getUTCSeconds();
                // check if any schedules match the current date & time
                const anyScheduleValid = a.alert.schedule!.some(s => {
                    switch (s.type!) {
                        case ServerAlertScheduleType.Fixed:
                            const startDate = new Date(s.fixed_Start!).getTime();
                            const endDate = new Date(s.fixed_End!).getTime();
                            const curDate = now.getTime();
                            return curDate >= startDate && curDate <= endDate;
                        case ServerAlertScheduleType.Daily:
                            return this._isTimeSpanWithin(currentTime, s.show_Start!, s.show_End!);
                        case ServerAlertScheduleType.DayOfWeek:
                            const weekday = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][now.getDay()];
                            return weekday === s.weekly_DayOfWeek! && this._isTimeSpanWithin(currentTime, s.show_Start!, s.show_End!);
                        case ServerAlertScheduleType.DayOfMonth:
                            return now.getDate() === s.monthly_Day! && this._isTimeSpanWithin(currentTime, s.show_Start!, s.show_End!);
                    }
                });
                // if no schedules are valid, skip this alert
                if (!anyScheduleValid) {
                    return;
                }
            }
            // if we got to this point, the alert matches all regex & schedule validation (if it has) and can be drawn
            results.push(a);
        });


        this.drawAlerts(results);
    }

    private _getTotalSecondsForTimeSpan(timespan: string) {
        if (!timespan) {
            return 0;
        }
        const r = /^(\d{1,2}):(\d{1,2}):(\d{1,2})$/.exec(timespan);
        if (!r) {
            return 0;
        }
        return (+r[1] * 3600) + (+r[2] * 60) + +r[3];
    }
    private _isTimeSpanWithin(currentTime: string, minTime: string, maxTime: string) {
        const min = this._getTotalSecondsForTimeSpan(minTime);
        const max = this._getTotalSecondsForTimeSpan(maxTime);
        const curTime = this._getTotalSecondsForTimeSpan(currentTime);
        return curTime >= min && curTime <= max;
    }

    private async drawAlerts(filteredAlerts: AlertInfo[]) {
        const alertContainer = await this.getOrCreateAlertContainer();
        const desiredAlertIds = filteredAlerts.map(f => f.element.id);
        const existingAlertIds: string[] = [];
        const toRemove: HTMLElement[] = [];
        // go through existing children
        alertContainer.childNodes.forEach(child => {
            const c = child as HTMLElement;
            if (!c) {
                return;
            }
            if (!c.id || !desiredAlertIds.includes(c.id)) {
                toRemove.push(c);
            } else {
                existingAlertIds.push(c.id);
            }
        });
        // remove the elements we need
        toRemove.forEach(r => r.remove());
        // go through desired alerts and add the new alerts that are needed to the container (hidden)
        const newlyAddedAlerts: AlertInfo[] = [];
        filteredAlerts.forEach(f => {
            const alertId = f.element.id;
            // if an alert already exists with that id, ignore
            if (existingAlertIds.includes(alertId)) {
                return;
            }
            this.hideAlert(f.element);
            alertContainer.appendChild(f.element);
            newlyAddedAlerts.push(f);
        });
        // show newly added after 100ms
        if (newlyAddedAlerts.length > 0) {
            setTimeout(() => {
                newlyAddedAlerts.forEach(f => this.showAlert(f.element));
            }, 100);
        }

        // store statistics of newly added alerts if needed
        this.storeStatistics(newlyAddedAlerts.map(e => e.alert).filter(e => e.collectStatistics), ServerAlertStatisticType.Seen);
        // auto hide newly added alerts if needed
        newlyAddedAlerts.forEach(f => this.autoHideAlert(f));
    }

    private async getOrCreateAlertContainer(): Promise<HTMLElement> {
        const doc = await this.common.waitForElement('body', 60, document);
        let c = document.getElementById(this.containerId) as HTMLElement;
        if (c) {
            return c;
        }
        // create alert container
        c = document.createElement("div") as HTMLElement;
        c.id = this.containerId;
        c.style.zIndex = '100';
        c.style.position = 'absolute';
        c.style.left = '0';
        c.style.right = '0';
        c.style.top = '0';
        c.style.maxWidth = '60vw';
        c.style.margin = '0 auto';
        c.style.width = 'fit-content';
        c.style.padding = '0px 4px 4px 4px';
        document.body.appendChild(c);
        return c;
    }

}

class LocalDismissManager {
    private key: string = 'stm_locally_dismissed_alerts';
    public getDismissedAlertIds(): string[] {
        return JSON.parse(window.localStorage.getItem(this.key) || '[]');
    }
    public setDismissedAlertIds(vals: string[]) {
        window.localStorage.setItem(this.key, JSON.stringify(vals || []));
    }
}

interface AlertInfo {
    alert: SimpleTableauServerAlertDTO;
    element: HTMLElement;
}